/**
 * @license
 * @preserve
 *
 * MIT License
 * 
 * Copyright (c) 2022 KeeeX SAS
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 * 
 * ----------------------------------------
 * 
 * ISC License from bitcoinjs/bip39 project and source
 * 
 * Copyright (c) 2014, Wei Lu <luwei.here@gmail.com> and Daniel Cousens <email@dcousens.com>
 * 
 * Permission to use, copy, modify, and/or distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 * 
 */
import { validateBitsChecksum } from "./crypto.js";
import { getWordlist } from "./gen/wordlists.js";
import { binaryToByte, containIdeograms, normalizeUTF8String, numberToBinary11 } from "./utils.js";
const EXPECTED_WORDLIST_LENGTH = 2048;
export const filterWordlist = (wordlist) => {
    if (Array.isArray(wordlist)) {
        if (wordlist.length !== EXPECTED_WORDLIST_LENGTH)
            throw new Error("Invalid wordlist");
        return Promise.resolve(wordlist);
    }
    return getWordlist(wordlist);
};
const cleanMnemonicWord = (wordslist) => (word) => {
    if (wordslist.includes(word))
        return normalizeUTF8String(word);
    // Check if input is single prefix
    const candidate1 = wordslist.filter(c => c.startsWith(word));
    if (candidate1.length === 1)
        return normalizeUTF8String(candidate1[0]);
    // Check using first four letters only
    const MIN_LETTERS = 4;
    if (word.length > MIN_LETTERS) {
        const prefix = word.substring(0, MIN_LETTERS);
        const candidate2 = wordslist.filter(c => c.startsWith(prefix));
        if (candidate2.length === 1)
            return normalizeUTF8String(candidate2[0]);
    }
    throw new Error(`Invalid word (${word})`);
};
const MINIMUM_WORD_COUNT = 3;
const MAXIMUM_WORD_COUNT = 24;
const EXPECTED_WORD_GROUP_LENGTH = 3;
const inputMnemonicToWords = async (mnemonic, wordlistSource) => {
    const wordslist = await filterWordlist(wordlistSource);
    const splitList = normalizeUTF8String(mnemonic)
        .split(" ")
        .filter(c => Boolean(c));
    if (splitList.length < MINIMUM_WORD_COUNT
        || splitList.length > MAXIMUM_WORD_COUNT
        || (splitList.length % EXPECTED_WORD_GROUP_LENGTH) !== 0) {
        throw new Error("Invalid mnemonic length");
    }
    return splitList.map(cleanMnemonicWord(wordslist));
};
/** Return the mnemonic string with only valid words and single space separators */
export const normalizeMnemonic = async (mnemonic, wordlistSource) => {
    const words = await inputMnemonicToWords(mnemonic, wordlistSource);
    return words.join(" ");
};
export const mnemonicToBuf8 = async (mnemonic, wordlistSource) => {
    const wordlist = await filterWordlist(wordlistSource);
    const words = await inputMnemonicToWords(mnemonic, wordlist);
    const bits = words.map(word => {
        const index = wordlist.indexOf(word);
        return numberToBinary11(index);
    }).join("");
    const entropy = await validateBitsChecksum(bits);
    return entropy;
};
export const bitsToMnemonic = async (bits, wordlistSource) => {
    const wordlist = await filterWordlist(wordlistSource);
    // eslint-disable-next-line prefer-named-capture-group, require-unicode-regexp
    const chunks = bits.match(/(.{1,11})/g);
    if (!chunks)
        throw new Error("Unexpected state");
    const words = chunks.map(binary => {
        const index = binaryToByte(binary);
        return wordlist[index];
    });
    return containIdeograms(wordlist.join(""))
        ? words.join("\u3000")
        : words.join(" ");
};
